import { Injectable } from '@angular/core';
import {
  ApproveRequest,
  AssignmentCustomerResponse,
  AssignmentDateContextResponse,
  AssignmentDateListItemResponse,
  AssignmentDateTimestampListItemResponse,
  AssignmentDocumentUpdateRequest,
  AssignmentPspResponse,
  CancelationRequest,
  CandidateProfileService,
  CommentResponse,
  ContractCreationResponse,
  CreateAssignmentRequest,
  CreateCommentRequest,
  CreatedResponse,
  CustomerAssignmentDateTimestampService,
  CustomerAssignmentFilterListsResponse,
  CustomerAssignmentListItemResponse,
  CustomerAssignmentService,
  CustomerProfileAssignmentItemResponse,
  CustomerProfilePersonalIdRequest,
  CustomerProfileRatingCreateRequest,
  CustomerProfileRatingListResponse,
  CustomerProfileRatingUpdateRequest,
  CustomerResponse,
  CustomerService,
  DisapproveRequest,
  DocumentCreationResponse,
  DocumentUploadRequest,
  EmptySuccessfulResponse,
  GeneralApiResponse,
  ListApiResponse,
  NoteRequest,
  PaginatedApiResponseWithFilters,
  PspAssignmentFilterListsResponse,
  PspAssignmentListItemResponse,
  PspAssignmentService,
  PspCustomerService,
  PspProfilePersonalIdRequest,
  RegenerationRequest,
  SigningService,
  UpdatedResponse,
  UpdateRequest,
  UpdateWorkTimesRequest,
} from '../../../api';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BaseSharedHelperService } from '../../shared/services/base-shared-helper.service';
import { ExportCustomerContractsRequestInterface } from './_interfaces/export-customer-contracts-request.interface';
import { ExportPspContractsRequestInterface } from './_interfaces/export-psp-contracts-request.interface';
import { ExportPspAssignmentInformationRequestInterface } from './_interfaces/export-psp-assignment-information-request.interface';
import { ExportCustomerAssignmentInformationRequestInterface } from './_interfaces/export-customer-assignment-information-request.interface';
import { BaseListPagination } from '../../shared/enums/base-list-pagination.enum';

@Injectable({ providedIn: 'root' })
export class BaseAssignmentsService {
  constructor(
    private pspAssignmentService: PspAssignmentService,
    private customerAssignmentService: CustomerAssignmentService,
    private candidateProfileService: CandidateProfileService,
    private customerService: CustomerService,
    private pspCustomerService: PspCustomerService,
    private signingService: SigningService,
    private customerAssignmentDateTimestampService: CustomerAssignmentDateTimestampService
  ) {}

  getPspAllAssignments(params: {
    psp: number;
    page?: number;
    size?: number;
    search?: string;
    mainQualification?: any;
    customer?: any;
    location?: any;
    pspBranch?: any;
    status?: any;
    openContract?: any;
    fromDate?: string;
    openAssignmentTimes?: boolean;
    order?: any;
  }): Observable<
    PaginatedApiResponseWithFilters & {
      data?: Array<PspAssignmentListItemResponse>;
      filters?: PspAssignmentFilterListsResponse;
    }
  > {
    return this.pspAssignmentService.getPspAssignmentListAll({
      psp: params.psp,
      page: params.page || BaseListPagination.page,
      size: params.size || BaseListPagination.size,
      search: params.search,
      'mainQualifications[]': params.mainQualification,
      'customers[]': params.customer,
      'pspBranches[]': params.pspBranch,
      'statuses[]': params.status,
      'customerLocations[]': params.location,
      fromDate: params.fromDate,
      openAssignmentTimes: params.openAssignmentTimes,
      'openContracts[]': params.openContract,
      orderKey: params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
    });
  }

  getAllCustomerAssignments(params: {
    customer: number;
    page?: any;
    size?: any;
    search?: string;
    mainQualification?: any;
    department?: number;
    location?: number;
    pspBranch?: any;
    status?: any;
    fromDate?: string;
    openAssignmentTimes?: boolean;
    openContract?: any;
    order?: any;
  }): Observable<
    PaginatedApiResponseWithFilters & {
      data?: Array<CustomerAssignmentListItemResponse>;
      filters?: CustomerAssignmentFilterListsResponse;
    }
  > {
    return this.customerAssignmentService.getCustomerAssignmentList({
      customer: params.customer,
      page: params.page || BaseListPagination.page,
      size: params.size || BaseListPagination.size,
      search: params.search,
      'mainQualifications[]': params.mainQualification,
      'departments[]': params.department,
      'customerLocations[]': params.location,
      'pspBranches[]': params.pspBranch,
      'statuses[]': params.status,
      fromDate: params.fromDate,
      openAssignmentTimes: params.openAssignmentTimes,
      'openContracts[]': params.openContract,
      orderKey: params.order?.split(',')[0],
      orderDirection: params.order?.split(',')[1],
    });
  }

  exportCustomerContracts(params: ExportCustomerContractsRequestInterface): Observable<any> {
    return this.customerAssignmentService.getCustomerContractExport$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  exportPspContracts(params: ExportPspContractsRequestInterface): Observable<any> {
    return this.candidateProfileService.getPspContractExport$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  getPspAssignment(psp: number, assignment: number): Observable<GeneralApiResponse & { data?: AssignmentPspResponse }> {
    return this.pspAssignmentService.getPspAsignmentAssignment({ psp, assignment });
  }

  getCustomerAssignment(
    customer: number,
    assignment: number
  ): Observable<GeneralApiResponse & { data?: AssignmentCustomerResponse }> {
    return this.customerAssignmentService.getCustomerAsignmentAssignment({ customer, assignment });
  }

  getPspsCustomer(psp: number, customer: number): Observable<GeneralApiResponse & { data?: CustomerResponse }> {
    return this.pspCustomerService.getPspCustomerShow({ psp, customer });
  }

  getCustomersCustomer(customer: number): Observable<GeneralApiResponse & { data?: CustomerResponse }> {
    return this.customerService.getCustomerGet({ customer });
  }

  confirmAssignment(assignment: number, assignmentDate: number, customer: number): Observable<EmptySuccessfulResponse> {
    return this.customerAssignmentService.postAssignmentAssignmentDateConfirmation({
      assignment,
      assignmentDate,
      customer,
    });
  }

  getPspAssignmentDates(
    psp: number,
    assignment: number,
    status: any
  ): Observable<ListApiResponse & { data?: Array<AssignmentDateListItemResponse> }> {
    return this.pspAssignmentService.getPspAsignmentAssignmentDateList({
      psp,
      assignment,
      status,
    });
  }

  getCustomerAssignmentDates(
    customer: number,
    assignment: number,
    status?: any
  ): Observable<ListApiResponse & { data?: Array<AssignmentDateListItemResponse> }> {
    return this.customerAssignmentService.getCustomerAsignmentAssignmentDatesList({
      customer,
      assignment,
      status,
    });
  }

  createUpdateAssignmentTimes(params: {
    psp: number;
    assignment: number;
    assignmentDate: number;
    body: UpdateWorkTimesRequest;
  }): Observable<CreatedResponse> {
    return this.pspAssignmentService.putPspAssignmentDateTimeReplace(params);
  }

  getCustomerAssignmentDateTimestamps(params: { customer: number; assignmentDate: number }): Observable<{
    message?: string;
    data?: {
      assignmentDate?: AssignmentDateContextResponse;
      timestamps?: Array<AssignmentDateTimestampListItemResponse>;
    };
  }> {
    return this.customerAssignmentDateTimestampService.getCustomerAssignmentDateTimestampList(params);
  }

  updateCustomerAssignmentDateTimestamps(params: {
    customer: number;
    assignmentDate: number;
    body: UpdateRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerAssignmentDateTimestampService.putCustomerAssignmentDateTimestampUpdate(params);
  }

  customerSignContractExternal(params: { contract: number; customer: number }): Observable<any> {
    return this.signingService.putCustomerSignatureExternal(params);
  }

  customerSignContractDigitally(params: { contract: number; customer: number }): Observable<any> {
    return this.signingService.putCustomerSignatureDigital(params);
  }

  pspSignContractExternal(params: { contract: number; psp: number }): Observable<any> {
    return this.signingService.putAppApiWebV1PspSignContractSignexternalIndex(params);
  }

  pspSignContractConcretization(params: { contract: number; psp: number }): Observable<any> {
    return this.signingService.putAppApiWebV1PspSignContractSignconcretisationIndex(params);
  }

  pspSignContractDigitally(params: { contract: number; psp: number }): Observable<any> {
    return this.signingService.putAppApiWebV1PspSignContractSigndigitalIndex(params);
  }

  cancelAssignmentCustomer(
    customer: number,
    assignment: number,
    body: CancelationRequest
  ): Observable<EmptySuccessfulResponse> {
    return this.customerAssignmentService
      .postCustomerAsignmentCancelAssignment({ customer, assignment, body })
      .pipe(take(1));
  }

  cancelAssignmentPsp(psp: number, assignment: number, body: CancelationRequest): Observable<EmptySuccessfulResponse> {
    return this.pspAssignmentService
      .postPspAsignmentCancelAssignment({
        psp,
        assignment,
        body,
      })
      .pipe(take(1));
  }

  activateMobileAppAccess(params: { profile: number; psp: number }): Observable<EmptySuccessfulResponse> {
    return this.candidateProfileService.postAppApiWebV1PspProfileMobileaccessReactivateIndex(params);
  }

  fetchCandidateEndedAssignments(
    customer: number,
    profile: number
  ): Observable<Array<CustomerProfileAssignmentItemResponse>> {
    return this.customerAssignmentService
      .getCustomerProfileEndedAssignmentList({
        customer,
        profile,
      })
      .pipe(map((response) => response.data));
  }

  fetchCandidateReviews(customer: number, profile: number): Observable<CustomerProfileRatingListResponse> {
    return this.customerService.getCustomerProfileRatingList({ customer, profile }).pipe(
      take(1),
      map((response) => response.data)
    );
  }

  downloadPspContract(psp: number, assignment: number, contract: number): Observable<any> {
    return this.pspAssignmentService
      .getPspAsignmentContractDownload$Response({
        psp,
        assignment,
        contract,
      })
      .pipe(
        map((response) => {
          const contentDispositionValue = response.headers.get('Content-Disposition');
          let fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
          return {
            body: response.body,
            fileName,
          };
        })
      );
  }
  exportPspAssignmentInfo(params: ExportPspAssignmentInformationRequestInterface): Observable<any> {
    return this.pspAssignmentService.getPspAssignmentListExport$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  exportCustomerAssignmentInfo(params: ExportCustomerAssignmentInformationRequestInterface): Observable<any> {
    return this.customerAssignmentService.getCustomerAssignmentListExport$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  downloadCustomerContact(customer: number, assignment: number, contract: number): Observable<any> {
    return this.customerAssignmentService
      .getCustomerAsignmentContractDownload$Response({
        customer,
        assignment,
        contract,
      })
      .pipe(
        map((response) => {
          const contentDispositionValue = response.headers.get('Content-Disposition');
          let fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
          return {
            body: response.body,
            fileName,
          };
        })
      );
  }

  createCandidateRating(
    customer: number,
    profile: number,
    body: CustomerProfileRatingCreateRequest
  ): Observable<CreatedResponse> {
    return this.customerService
      .postCustomerProfileRatingCreate({
        customer,
        profile,
        body,
      })
      .pipe(take(1));
  }

  updateCandidateRating(
    customer: number,
    profile: number,
    rating: number,
    body: CustomerProfileRatingUpdateRequest
  ): Observable<EmptySuccessfulResponse> {
    return this.customerService
      .putCustomerProfileRatingUpdate({
        customer,
        profile,
        rating,
        body,
      })
      .pipe(take(1));
  }

  updateCustomerProfileInternalPersonalId(params: {
    customer: number;
    profile: number;
    body: CustomerProfilePersonalIdRequest;
  }): Observable<UpdatedResponse> {
    return this.customerService.putCustomerProfileIdUpdate(params);
  }

  downloadCustomerCandidateDocumentFile(params: {
    assignment: number;
    document: string;
    customer: number;
  }): Observable<{ body: Blob; fileName: string }> {
    return this.customerAssignmentService.getCustomerAssignmentDocumentsDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  regenerateCustomerAssignmentContract(params: {
    customer: number;
    assignment: number;
    body?: RegenerationRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerAssignmentService.postCustomerAsignmentContractRegeneration(params);
  }

  regeneratePspAssignmentContract(params: {
    psp: number;
    assignment: number;
    body?: RegenerationRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.pspAssignmentService.postPspAsignmentContractRegeneration(params);
  }

  regenerateCustomerAssignmentProfileIDCard(params: {
    customer: number;
    assignment: number;
    profile: number;
  }): Observable<Blob> {
    return this.customerAssignmentService.postCustomerAssignmentProfileIdCardRegenerate(params);
  }

  regeneratePSPAssignmentProfileIDCard(params: { psp: number; assignment: number; profile: number }): Observable<Blob> {
    return this.pspAssignmentService.postPspAssignmentProfileIdCardRegenerate(params);
  }

  getCustomerAssignmentProfileIDCard(params: {
    customer: number;
    assignment: number;
    profile: number;
  }): Observable<{ body: Blob; fileName: string }> {
    return this.customerAssignmentService.getCustomerAssignmentProfileIdCardFileDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  getPSPAssignmentProfileIDCard(params: { psp: number; assignment: number; profile: number }): Observable<any> {
    return this.pspAssignmentService.getPspAssignmentProfileIdCardFileDownload$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');
        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  approveInquiryOffer(params: {
    customer: number;
    offer: number;
    body: ApproveRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerService.postCustomerOfferApprove(params);
  }

  disapproveInquiryOffer(params: {
    customer: number;
    offer: number;
    body: DisapproveRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerService.deleteCustomerOfferDisapprove(params);
  }

  noteInquiryOffer(params: {
    customer: number;
    offer: number;
    body: NoteRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerService.postCustomerOfferNote(params);
  }

  createCustomerOfferComment(params: {
    customer: number;
    offer: number;
    body: CreateCommentRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerService.postCustomerOfferCommentCreate(params);
  }

  getCustomerOfferCommentList(params: { customer: number; offer: number }): Observable<
    ListApiResponse & {
      data?: Array<CommentResponse>;
    }
  > {
    return this.customerService.getCustomerOfferCommentList(params);
  }

  getLocationDepartmentQualificationList(params: {
    psp: number;
    customer: number;
    location: number;
    department: number;
    category?: number;
    tag?: 'business' | 'medical';
    search?: string;
  }) {
    return this.pspCustomerService
      .getPspCustomerLocationDepartmentQualificationList(params)
      .pipe(map((response) => response.data));
  }

  getPspCustomerLocationDepartment(params: { psp: number; customer: number; location: number; department: number }) {
    return this.pspCustomerService.getPspCustomerLocationDepartmentShow(params).pipe(map((response) => response.data));
  }

  getAssignmentDateList(params: { psp: number; profile: number }) {
    return this.pspAssignmentService.getPspProfileAssignmentDateList(params).pipe(map((response) => response.data));
  }

  createPspAssignment(params: { psp: number; body: CreateAssignmentRequest }) {
    return this.pspAssignmentService.postPspAssignmentsCreate(params);
  }

  uploadPSPAssignmentContract(params: {
    psp: number;
    body: DocumentUploadRequest;
  }): Observable<ContractCreationResponse> {
    return this.pspAssignmentService.postPspAssignmentsContractsCreate(params).pipe(map((response) => response.data));
  }

  uploadCustomerAssignmentContract(params: {
    customer: number;
    body: DocumentUploadRequest;
  }): Observable<ContractCreationResponse> {
    return this.customerAssignmentService
      .postCustomerAssignmentsContractsCreate(params)
      .pipe(map((response) => response.data));
  }

  updatePSPProfilePersonalId(params: {
    psp: number;
    assignment: number;
    profile: number;
    body: PspProfilePersonalIdRequest;
  }): Observable<UpdatedResponse> {
    return this.pspAssignmentService.putPspProfileIdUpdate(params);
  }

  /**
   * Uploads the file only. The document is not on the assignment until
   * updatePspAssignmentDocument describes it.
   */
  uploadPspAssignmentDocument(params: {
    psp: number;
    assignment: number;
    body: DocumentUploadRequest;
  }): Observable<DocumentCreationResponse> {
    return this.pspAssignmentService.postPspAssignmentDocumentAdd(params).pipe(map((response) => response.data));
  }

  updatePspAssignmentDocument(params: {
    psp: number;
    assignment: number;
    document: string;
    body: AssignmentDocumentUpdateRequest;
  }): Observable<void> {
    return this.pspAssignmentService.putPspAssignmentDocumentUpdate(params);
  }
}
